import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import DashboardHeader from "../../components/dashboard-header";
import { VisuallyHiddenInput } from "../../components/firebase-image-upload";
import { useFirebaseImageUpload } from "../../components/firebase-image-upload/use-firebase-image-upload";
import { useNotification } from "../../contexts/NotificationContext";
import { useAddIdeaBookItem } from "../../hooks/idea-books/use-add-idea-book-item";
import { useDeleteIdeaBookItem } from "../../hooks/idea-books/use-delete-idea-book-item";
import { useGetIdeaBook } from "../../hooks/idea-books/use-get-idea-book";
import { useGetIdeaBookItems } from "../../hooks/idea-books/use-get-idea-book-items";
import { useUpdateIdeaBookItemNote } from "../../hooks/idea-books/use-update-idea-book-item-note";
import type { IdeaBook, IdeaBookItem } from "../../hooks/idea-books/types";
import { useUserContext } from "../../hooks/userContext";
import { FirebasePaths } from "../../utils/firebase";
import type { MediaFile } from "../../__generated__/types";

export const MyIdeaBookDetailPage = () => {
  const { ideaBookId } = useParams<{ ideaBookId: string }>();
  const { user } = useUserContext();
  const { showError, showSuccess } = useNotification();
  const { getIdeaBook } = useGetIdeaBook();
  const { getIdeaBookItems } = useGetIdeaBookItems();
  const { addIdeaBookItem } = useAddIdeaBookItem();
  const { updateIdeaBookItemNote } = useUpdateIdeaBookItemNote();
  const { deleteIdeaBookItem } = useDeleteIdeaBookItem();
  const [ideaBook, setIdeaBook] = useState<IdeaBook | null>(null);
  const [ideaBookItems, setIdeaBookItems] = useState<IdeaBookItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [noteItem, setNoteItem] = useState<IdeaBookItem | null>(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const homeownerId = user?.id ?? "";

  const refreshIdeaBook = useCallback(
    async (refresh = true) => {
      if (!ideaBookId) return;
      const [book, items] = await Promise.all([
        getIdeaBook(ideaBookId, refresh),
        getIdeaBookItems(ideaBookId, refresh),
      ]);
      setIdeaBook(book);
      setIdeaBookItems(items);
    },
    [getIdeaBook, getIdeaBookItems, ideaBookId]
  );

  useEffect(() => {
    if (!ideaBookId) {
      setIsLoading(false);
      return;
    }
    let isActive = true;
    setIsLoading(true);
    Promise.all([
      getIdeaBook(ideaBookId, true),
      getIdeaBookItems(ideaBookId, true),
    ])
      .then(([book, items]) => {
        if (!isActive) return;
        setIdeaBook(book);
        setIdeaBookItems(items);
      })
      .catch(() => {
        if (!isActive) return;
        showError("Unable to load idea book. Please try again.");
      })
      .finally(() => {
        if (!isActive) return;
        setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [getIdeaBook, getIdeaBookItems, ideaBookId, showError]);

  const handleAddUploadedImages = useCallback(
    async (uploaded: MediaFile[]) => {
      if (!ideaBookId || !homeownerId) return;
      const validFiles = uploaded.filter((file) => file?._id);
      if (!validFiles.length) return;

      try {
        await Promise.all(
          validFiles.map((file) =>
            addIdeaBookItem({
              ideaBookId,
              homeownerId,
              imageId: String(file._id),
            })
          )
        );
        showSuccess("Images added to your idea book.");
        await refreshIdeaBook(true);
      } catch (error) {
        showError("Unable to add images. Please try again.");
      }
    },
    [addIdeaBookItem, homeownerId, ideaBookId, refreshIdeaBook, showError, showSuccess]
  );

  const { loading: isUploading, onUpload } = useFirebaseImageUpload(
    FirebasePaths.IDEA_BOOKS,
    handleAddUploadedImages
  );

  const handleOpenNoteDialog = (item: IdeaBookItem) => {
    setNoteItem(item);
    setNoteDraft(item.note ?? "");
    setNoteDialogOpen(true);
  };

  const handleCloseNoteDialog = () => {
    setNoteDialogOpen(false);
    setNoteItem(null);
    setNoteDraft("");
  };

  const handleSaveNote = async () => {
    if (!noteItem) return;
    setIsSavingNote(true);
    try {
      const updated = await updateIdeaBookItemNote({
        itemId: noteItem._id,
        note: noteDraft.trim(),
      });
      if (updated) {
        setIdeaBookItems((prev) =>
          prev.map((item) =>
            item._id === updated._id ? { ...item, note: updated.note } : item
          )
        );
        showSuccess("Note saved.");
      }
      handleCloseNoteDialog();
    } catch (error) {
      showError("Unable to save note. Please try again.");
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!itemId) return;
    try {
      const success = await deleteIdeaBookItem({ itemId });
      if (success) {
        showSuccess("Image removed from idea book.");
        await refreshIdeaBook(true);
      }
    } catch (error) {
      showError("Unable to remove image. Please try again.");
    }
  };

  const ideaBookTitle = ideaBook?.name ?? "Idea Book";
  const emptyState = !isLoading && ideaBookItems.length === 0;
  const gridTemplate = useMemo(
    () => ({
      xs: "repeat(1, minmax(0, 1fr))",
      sm: "repeat(2, minmax(0, 1fr))",
      md: "repeat(3, minmax(0, 1fr))",
      lg: "repeat(4, minmax(0, 1fr))",
    }),
    []
  );

  return (
    <Box sx={{ backgroundColor: "#F8F8F8", minHeight: "100vh" }}>
      <DashboardHeader title={ideaBookTitle} showBackButton />

      <Box sx={{ px: { xs: 2, md: 3 }, pb: 6 }}>
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "18px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.08)",
            p: { xs: 2.5, md: 3.5 },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button
              component="label"
              variant="text"
              startIcon={<AddIcon />}
              disabled={isUploading}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: "#6B4FE8",
                "&:hover": { backgroundColor: "rgba(107, 79, 232, 0.08)" },
              }}
            >
              Upload Your Own Pictures
              <VisuallyHiddenInput
                type="file"
                multiple
                onChange={(event) =>
                  onUpload(event.target.files ?? ({} as FileList))
                }
              />
            </Button>
          </Box>

          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 6,
              }}
            >
              <CircularProgress sx={{ color: "#C2185B" }} />
            </Box>
          ) : !ideaBook ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Idea book not found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We could not find this idea book.
              </Typography>
            </Box>
          ) : emptyState ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                No images yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Save designer file images or upload your own to start this idea book.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gap: 2.5,
                gridTemplateColumns: gridTemplate,
              }}
            >
              {ideaBookItems.map((item) => {
                const imageUrl = item.image?.url ?? item.image?.path ?? "";
                const hasImage = Boolean(imageUrl);
                const hasNote = Boolean(item.note?.trim());
                const noteLabel = hasNote ? "View Notes" : "Edit Notes";

                return (
                  <Box key={item._id}>
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (!hasImage) return;
                        setPreviewImageUrl(imageUrl);
                      }}
                      onKeyDown={(event) => {
                        if (!hasImage) return;
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setPreviewImageUrl(imageUrl);
                        }
                      }}
                      sx={{
                        position: "relative",
                        borderRadius: "14px",
                        overflow: "hidden",
                        backgroundColor: "#F3F4F6",
                        cursor: hasImage ? "pointer" : "default",
                        outline: "none",
                        "&:focus-visible": {
                          boxShadow:
                            "0 0 0 3px rgba(194, 24, 91, 0.2), 0 2px 8px rgba(0, 0, 0, 0.08)",
                        },
                      }}
                    >
                      {hasImage ? (
                        <Box
                          component="img"
                          src={imageUrl}
                          alt={item.image?.title ?? "Idea book image"}
                          loading="lazy"
                          sx={{
                            width: "100%",
                            height: 200,
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: 200,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <ImageOutlinedIcon sx={{ color: "#9CA3AF" }} />
                        </Box>
                      )}

                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDeleteItem(item._id);
                        }}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                          },
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Button
                      variant="text"
                      startIcon={
                        hasNote ? <VisibilityOutlinedIcon /> : <EditOutlinedIcon />
                      }
                      onClick={() => handleOpenNoteDialog(item)}
                      sx={{
                        textTransform: "none",
                        color: "#4B5563",
                        fontSize: "13px",
                        pl: 0,
                        mt: 0.5,
                      }}
                    >
                      {noteLabel}
                    </Button>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      <Dialog
        open={noteDialogOpen}
        onClose={handleCloseNoteDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {noteItem?.note?.trim() ? "View Notes" : "Edit Notes"}
        </DialogTitle>
        <DialogContent>
          <TextField
            value={noteDraft}
            onChange={(event) => setNoteDraft(event.target.value)}
            placeholder="Write your notes here..."
            multiline
            minRows={4}
            fullWidth
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseNoteDialog} variant="text">
            Cancel
          </Button>
          <Button
            onClick={handleSaveNote}
            variant="contained"
            disabled={isSavingNote}
          >
            {isSavingNote ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(previewImageUrl)}
        onClose={() => setPreviewImageUrl(null)}
        fullScreen
        PaperProps={{
          sx: {
            backgroundColor: "#F5F5F5",
          },
        }}
      >
        <DialogContent
          sx={{
            p: { xs: 2, md: 4 },
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconButton
              onClick={() => setPreviewImageUrl(null)}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                color: "#111827",
                backgroundColor: "rgba(255, 255, 255, 0.92)",
                border: "1px solid #E5E7EB",
                boxShadow: "0 10px 24px rgba(15, 23, 42, 0.16)",
                "&:hover": {
                  backgroundColor: "#fff",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            {previewImageUrl && (
              <Box
                component="img"
                src={previewImageUrl}
                alt="Idea book preview"
                sx={{
                  maxWidth: "96vw",
                  maxHeight: "90vh",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                  display: "block",
                  borderRadius: "16px",
                  backgroundColor: "#fff",
                  boxShadow: "0 24px 60px rgba(15, 23, 42, 0.18)",
                }}
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
